import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { randomKey } from 'lib/riskbase';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doAppFileGetFilesFromPicker,
  doEngageDownloadFlatReferences,
  doEngageDownloadTemplate,
  doEngageUploadContacts,
  eventFilesAdded,
  NAVIGATE_MODAL_BACK,
} from 'store/actions';
import { ICONS } from 'components/Icon';
import { FileTypeGroupsEnum } from 'service/file/types';
import { doAddEvent, doRemoveEvents } from 'lib/events';
import { CardGeneric } from 'components/CardGeneric';
import { Txt } from 'components/Txt';
import { PageHeader } from 'components/PageHeader';

export const EngageContactUploadsModal: React.FC<
  ModalScreenProps<ModalScreens.EngageContactUpload>
> = ({ route }) => {
  const eventKey = React.useRef(randomKey());

  const { key, propertyId } = route.params;

  const dispatch = useDispatch();

  React.useEffect(() => {
    const events = [
      doAddEvent(eventFilesAdded, (event) => {
        if (event.payload.key === eventKey.current) {
          const file = event.payload.files[0];
          if (file) {
            // dispatch action to upload file
            dispatch(
              doEngageUploadContacts({
                key,
                propertyId: propertyId,
                fileName: file.name,
                file: file,
              }),
            );
          }
        }
      }),
    ];
    return () => {
      doRemoveEvents(events);
    };
  }, [dispatch, propertyId, key]);

  const handleClose = React.useCallback(() => {
    dispatch(NAVIGATE_MODAL_BACK());
  }, [dispatch]);

  useEsc(handleClose);

  return (
    <Page
      scrollView
      header={
        <PageHeader title="Import Contacts" onPressCancelButton={handleClose} />
      }
    >
      <CardGeneric>
        <Txt>
          Upload a completed Contacts CSV using the import template available
          below.
        </Txt>
      </CardGeneric>

      <Card>
        <CardRow
          label="Upload Contacts CSV"
          icon={ICONS.UpSolid}
          iconColor={theme.colors.blue}
          labelColor={theme.colors.blue}
          onPress={() =>
            dispatch(
              doAppFileGetFilesFromPicker({
                eventKey: eventKey.current,
                returnType: 'event',
                multiSelect: false,
                allowedFileGroups: [FileTypeGroupsEnum.text],
              }),
            )
          }
        />
      </Card>

      <Card
        label="Downloads"
        labelStyle={{ paddingBottom: theme.padding }}
        explanation={
          'Flat contacts must be uploaded in a specific format. Please download and use the import template above.'
        }
      >
        <CardRow
          label="Download Import Template"
          icon={ICONS.DownSolid}
          iconColor={theme.colors.blue}
          labelColor={theme.colors.blue}
          onPress={() =>
            dispatch(
              doEngageDownloadTemplate({
                key: eventKey.current,
              }),
            )
          }
        />
      </Card>

      <Card
        explanation={
          'Download a list of flats and their references. Each contact must be associated with a flat reference when imported.'
        }
      >
        <CardRow
          label="Download Flat References"
          icon={ICONS.DownSolid}
          iconColor={theme.colors.blue}
          labelColor={theme.colors.blue}
          onPress={() =>
            dispatch(
              doEngageDownloadFlatReferences({
                key: eventKey.current,
                propertyId: propertyId,
              }),
            )
          }
        />
      </Card>
    </Page>
  );
};
